import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

function EvaluationSystemComponent({
  upperValue,
  lowerValue,
  setUpperValue,
  setLowerValue,
}) {
  const upperInputRef = useRef(null);
  const lowerInputRef = useRef(null);

  // Track which input has focus
  const [activeInput, setActiveInput] = useState(null);

  // Store current internal state for inputs
  const [upperInputValue, setUpperInputValue] = useState(upperValue);
  const [lowerInputValue, setLowerInputValue] = useState(lowerValue);

  // Update internal state when props change (but only if the input is not active)
  useEffect(() => {
    if (activeInput !== "upper") {
      setUpperInputValue(upperValue);
    }
  }, [upperValue, activeInput]);

  useEffect(() => {
    if (activeInput !== "lower") {
      setLowerInputValue(lowerValue);
    }
  }, [lowerValue, activeInput]);

  // Maintain focus when typing
  useEffect(() => {
    if (activeInput === "upper" && upperInputRef.current) {
      upperInputRef.current.focus();

      // Preserve cursor position
      const length = upperInputRef.current.value.length;
      upperInputRef.current.setSelectionRange(length, length);
    } else if (activeInput === "lower" && lowerInputRef.current) {
      lowerInputRef.current.focus();

      // Preserve cursor position
      const length = lowerInputRef.current.value.length;
      lowerInputRef.current.setSelectionRange(length, length);
    }
  }, [activeInput, upperInputValue, lowerInputValue]);

  // Handle upper value change
  const handleUpperValueChange = (e) => {
    e.stopPropagation();

    const value = e.target.value;
    setUpperInputValue(value);

    // Only update parent state if value is valid
    if (value === "" || !isNaN(Number(value))) {
      setUpperValue(value === "" ? "" : Number(value));
    }
  };

  // Handle lower value change
  const handleLowerValueChange = (e) => {
    e.stopPropagation();

    const value = e.target.value;
    setLowerInputValue(value);

    // Only update parent state if value is valid
    if (value === "" || !isNaN(Number(value))) {
      setLowerValue(value === "" ? "" : Number(value));
    }
  };

  // Focus handlers
  const handleUpperFocus = (e) => {
    e.stopPropagation();
    setActiveInput("upper");
    e.target.select();
  };

  const handleLowerFocus = (e) => {
    e.stopPropagation();
    setActiveInput("lower");
    e.target.select();
  };

  // Blur handlers
  const handleBlur = () => {
    // Small delay to allow for clicks within the component
    setTimeout(() => {
      setActiveInput(null);
    }, 100);
  };

  // Common input field style
  const inputFieldClasses =
    "w-[40px] h-[20px] text-center border border-gray-300 rounded px-1 focus:outline-none focus:ring bg-[#CBD4E7] font-gazer-dynamic";

  return (
    <div
      className="p-1 relative h-full flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Pinned Total text to top and centered horizontally */}
      <div className="font-gazer-header mb-1 mt-0 absolute top-0 left-0 right-0 text-center">
        Total
      </div>

      {/* Content container - pushed down to make room for the Total header */}
      <div className="mt-6">
        {/* Upper threshold row */}
        <div className="flex items-center mb-1 relative">
          <span className="font-gazer mr-1">От</span>
          <input
            ref={upperInputRef}
            type="text"
            value={upperInputValue}
            onChange={handleUpperValueChange}
            onFocus={handleUpperFocus}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
            className={inputFieldClasses}
            style={{
              position: "relative",
              pointerEvents: "auto",
            }}
            data-testid="upper-input"
          />
          {/* Blue square */}
          <div className="ml-2 w-5 h-5 bg-[#4F7AE2] rounded-md" />
        </div>

        {/* Lower threshold row */}
        <div className="flex items-center relative">
          <span className="font-gazer mr-1">От</span>
          <input
            ref={lowerInputRef}
            type="text"
            value={lowerInputValue}
            onChange={handleLowerValueChange}
            onFocus={handleLowerFocus}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
            className={inputFieldClasses}
            style={{
              position: "relative",
              pointerEvents: "auto",
            }}
            data-testid="lower-input"
          />
          {/* Yellow square */}
          <div className="ml-2 w-5 h-5 bg-[#F1E323] rounded-md" />
        </div>
      </div>
    </div>
  );
}

EvaluationSystemComponent.propTypes = {
  upperValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  lowerValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  setUpperValue: PropTypes.func.isRequired,
  setLowerValue: PropTypes.func.isRequired,
};

export default EvaluationSystemComponent;
